// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../store/AppStore';
import { ScreenShell } from '../components/WebShell';
import { useReportsData, ReportRange } from '../hooks/useReportsData';
import { REPORTS } from '../api-endpoints/api-endpoints';

const EXPORT_PATH = '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>';

// Animated count-up for numeric KPI values; renders strings/dashes verbatim.
function CountUp({ value, duration = 950 }: { value: any; duration?: number }) {
  const isNum = typeof value === 'number' && isFinite(value);
  const [n, setN] = useState(isNum ? 0 : value);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (!isNum) { setN(value); return; }
    const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const step = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setN(Math.round(value * eased));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [value, duration, isNum]);
  return <>{n}</>;
}

// ── Bar chart SVG ─────────────────────────────────────────────────────────────
function BarChart({ data }: { data: Array<{ label: string; value: number }>; color?: string }) {
  const max = Math.max(...data.map(d => d.value), 1);
  const W = 540; const H = 118;            // shorter, more compact chart
  const plotH = H - 42;
  const slot = (W - 40) / data.length;
  const barW = Math.min(slot - 14, 64);    // slimmer bars, capped width
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
      <defs>
        <linearGradient id="skBarGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#4f46e5" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75, 1].map(p => (
        <line key={p}
          x1={28} x2={W - 8}
          y1={16 + plotH * (1 - p)} y2={16 + plotH * (1 - p)}
          stroke="oklch(0.92 0.004 250)" strokeWidth="1" strokeDasharray="2 4" />
      ))}
      {data.map((d, i) => {
        const x = 28 + i * slot + (slot - barW) / 2;
        const h = Math.max(plotH * (d.value / max), 3);
        const y = 16 + plotH - h;
        return (
          <g key={i}>
            <rect className="sk-bar-rect" style={{ animationDelay: `${0.25 + i * 0.08}s` }} x={x} y={y} width={barW} height={h} rx="5" fill="url(#skBarGrad)" />
            <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize="10.5" fill="oklch(0.55 0.010 250)">{d.label}</text>
            <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize="10.5" fill="oklch(0.38 0.010 250)" fontWeight="700">{d.value}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Donut chart SVG ───────────────────────────────────────────────────────────
function DonutChart({ data }: { data: Array<{ label: string; value: number; color: string }> }) {
  const sum = data.reduce((s, d) => s + d.value, 0);
  const size = 160; const r = 56; const cx = size / 2; const cy = size / 2;
  const stroke = 24; const C = 2 * Math.PI * r;
  let offset = 0;

  const segs = sum === 0 ? [] : data.map((d, i) => {
    const dash = (d.value / sum) * C;
    const seg = (
      <circle key={i} className="sk-seg" cx={cx} cy={cy} r={r}
        stroke={d.color} strokeWidth={stroke} fill="none"
        strokeDasharray={`${dash} ${C - dash}`}
        strokeDashoffset={-offset}
        strokeLinecap="butt"
        style={{ '--d': dash, '--g': C - dash, animationDelay: `${0.4 + i * 0.14}s` } as any} />
    );
    offset += dash;
    return seg;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
      <svg className="sk-donut" width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} stroke="oklch(0.970 0.004 90)" strokeWidth={stroke} fill="none" />
        {segs}
        <text x={cx} y={cy} textAnchor="middle" dy="5" fontSize="16" fontWeight="600"
          fill="oklch(0.20 0.010 250)"
          style={{ transform: `rotate(90deg)`, transformOrigin: `${cx}px ${cy}px` }}>
          <CountUp value={sum} />
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
            <span style={{ flex: 1, color: 'var(--ink-2)' }}>{d.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--ink)' }}>{d.value}</span>
            <span style={{ color: 'var(--ink-4)', minWidth: 30 }}>{sum > 0 ? Math.round(d.value / sum * 100) : 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Stacked bar rows ──────────────────────────────────────────────────────────
function StackedBar({ rows }: { rows: Array<{ label: string; value: number; max: number; color: string }> }) {
  return (
    <div className="sk-stacked-bar">
      {rows.map((r, i) => {
        const pct = Math.min((r.value / r.max) * 100, 100);
        return (
          <div key={i} className="sk-sb-row">
            <div className="sk-sb-label-row">
              <span>{r.label}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--ink)' }}>{r.value}{r.max === 100 ? '%' : ''}</span>
            </div>
            <div className="sk-sb-track">
              <div className="sk-sb-fill" style={{ width: `${pct}%`, background: r.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Reports screen ────────────────────────────────────────────────────────────

const AGENT_COLORS = [
  'linear-gradient(135deg,#0ea5e9,#6366f1)',
  'linear-gradient(135deg,#f97316,#db2777)',
  'linear-gradient(135deg,#10b981,#0ea5e9)',
  'linear-gradient(135deg,#a855f7,#ec4899)',
  'linear-gradient(135deg,#06b6d4,#3b82f6)',
];

function rangeToDateStrings(range: ReportRange): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  if (range === '7d') from.setDate(from.getDate() - 7);
  else if (range === '30d') from.setDate(from.getDate() - 30);
  else from.setDate(from.getDate() - 90);
  return {
    from: from.toISOString().split('T')[0],
    to: to.toISOString().split('T')[0],
  };
}

const CSAT_COLORS: Record<string, string> = {
  '5': 'oklch(0.50 0.14 150)',
  '4': '#6abf69',
  '3': 'oklch(0.62 0.13 60)',
  '2': 'oklch(0.58 0.14 40)',
  '1': 'oklch(0.55 0.13 25)',
};
const CSAT_LABELS: Record<string, string> = {
  '5': '★★★★★', '4': '★★★★', '3': '★★★', '2': '★★', '1': '★',
};

export function ReportsScreen() {
  const { tickets, agentMap, toast } = useApp();
  const [range, setRange] = useState<ReportRange>('7d');
  const data = useReportsData(range);

  const openCount    = tickets.filter(t => t.status === 'open').length;
  const pendingCount = tickets.filter(t => t.status === 'pending').length;

  // Volume: real buckets from the hook (empty until loaded / when no data)
  const volume = data.volume;

  // Channel breakdown: computed purely from live tickets by .channel
  const byChannel = [
    { label: 'Email', value: tickets.filter(t => t.channel === 'email').length, color: 'oklch(0.52 0.16 260)' },
    { label: 'API',   value: tickets.filter(t => t.channel === 'api').length,   color: 'oklch(0.55 0.15 250)' },
    { label: 'Web',   value: tickets.filter(t => t.channel === 'web').length,   color: 'oklch(0.55 0.14 200)' },
    { label: 'Agent', value: tickets.filter(t => t.channel === 'agent').length, color: 'oklch(0.62 0.13 60)' },
  ];

  const tagCounts: Record<string, number> = {};
  tickets.forEach(t => (t.tags || []).forEach(tag => { tagCounts[tag] = (tagCounts[tag] || 0) + 1; }));
  const topTags = Object.entries(tagCounts).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 5);
  const tagMax  = Math.max(...topTags.map(t => t.value), 1);
  const tagColors = ['oklch(0.52 0.16 260)', 'oklch(0.55 0.15 250)', 'oklch(0.62 0.13 60)', 'oklch(0.50 0.14 150)', 'oklch(0.55 0.14 200)'];

  // SLA rows: built from real attainment rates (0..1). Null rates are omitted.
  const slaRows: Array<{ label: string; value: number; max: number; color: string }> = [];
  if (data.slaFirstResponseRate != null) {
    slaRows.push({ label: 'First response within SLA', value: Math.round(data.slaFirstResponseRate * 100), max: 100, color: 'oklch(0.50 0.14 150)' });
  }
  if (data.slaResolutionRate != null) {
    slaRows.push({ label: 'Resolution within SLA', value: Math.round(data.slaResolutionRate * 100), max: 100, color: 'oklch(0.52 0.16 260)' });
  }

  // CSAT distribution: built from real keys '1'..'5'
  const csatData = ['5', '4', '3', '2', '1']
    .map(k => ({ label: CSAT_LABELS[k], value: Number(data.csatDistribution[k] || 0), color: CSAT_COLORS[k] }))
    .filter(d => d.value > 0);

  // Agent performance from real data; display names via agentMap
  const agents = data.agents.map((a, i) => ({
    name: agentMap[a.platformUserId] || a.platformUserId,
    initials: (agentMap[a.platformUserId] || a.platformUserId || 'A').slice(0, 2).toUpperCase(),
    color: AGENT_COLORS[i % AGENT_COLORS.length],
    handled: a.resolved,
    avg: a.avgMinutesToResolve != null ? `${Math.round(a.avgMinutesToResolve)}m` : '—',
  }));

  // Avg resolve time across agents (real) — used as a KPI
  const resolveMinsList = data.agents.map(a => a.avgMinutesToResolve).filter((v): v is number => v != null);
  const avgResolveMins = resolveMinsList.length > 0
    ? Math.round(resolveMinsList.reduce((s, v) => s + v, 0) / resolveMinsList.length)
    : null;

  const rangeLabel = range === '7d' ? 'Past 7 days' : range === '30d' ? 'Past 30 days' : 'Past 90 days';

  const onExport = () => {
    const { from, to } = rangeToDateStrings(range);
    const url = `${REPORTS.volume}?from=${from}&to=${to}&format=csv`;
    if (typeof window !== 'undefined') window.open(url, '_blank');
    toast('Export started.', 'info', 'send');
  };

  return (
    <ScreenShell crumbs={[{ label: 'Reports' }]}>
      {/* Page header */}
      <div className="sk-page-head">
        <div>
          <div className="sk-page-head-title">Reports</div>
          <div className="sk-page-head-sub">Volume, response time, channel performance, CSAT — for the selected window.</div>
        </div>
        <div className="t-row">
          <div className="sk-range-bar">
            {(['7d', '30d', '90d'] as const).map(k => (
              <button key={k} className={`sk-range-btn${range === k ? ' is-active' : ''}`} onClick={() => setRange(k)}>
                {k === '7d' ? '7 days' : k === '30d' ? '30 days' : '90 days'}
              </button>
            ))}
          </div>
          <button className="t-btn t-btn--sm" onClick={onExport}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: EXPORT_PATH }} />
            Export
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="sk-reports-body">
        {/* KPI strip */}
        <div className="sk-kpi-strip">
          {[
            { label: 'Open tickets',     value: openCount + pendingCount,                              delta: 'Open + pending',          color: 'var(--info)' },
            { label: 'Resolved (period)', value: data.totalResolved,                                   delta: `${data.totalCreated} created`, color: 'var(--success)' },
            { label: 'CSAT',             value: data.csatMean != null ? `${data.csatMean.toFixed(1)} / 5` : '—', delta: `${data.csatTotal} ratings`, color: 'var(--success)' },
            { label: 'Avg resolve time', value: avgResolveMins != null ? `${avgResolveMins}m` : '—',   delta: `${data.agents.length} agents`, color: 'var(--accent)' },
          ].map((k, i) => (
            <div key={i} className="sk-kpi-card">
              <div className="sk-kpi-label">{k.label}</div>
              <div className="sk-kpi-value"><CountUp value={k.value} /></div>
              <div className="sk-kpi-delta" style={{ color: k.color }}>{k.delta}</div>
            </div>
          ))}
        </div>

        {/* Row 1: Volume + Channel */}
        <div className="sk-charts-row">
          <div className="t-card" style={{ flex: 3 }}>
            <div className="t-card-head">
              <h3>Ticket volume</h3>
              <span className="t-card-sub">{rangeLabel} · new tickets</span>
            </div>
            <div className="t-card-body">
              {volume.length === 0
                ? <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>{data.loading ? 'Loading…' : 'No volume data for this window.'}</span>
                : <BarChart data={volume} color="oklch(0.52 0.16 260)" />}
            </div>
          </div>
          <div className="t-card" style={{ flex: 2 }}>
            <div className="t-card-head">
              <h3>Channel breakdown</h3>
              <span className="t-card-sub">Where tickets come from</span>
            </div>
            <div className="t-card-body">
              <DonutChart data={byChannel} />
            </div>
          </div>
        </div>

        {/* Row 2: SLA + Top tags + CSAT */}
        <div className="sk-charts-row">
          <div className="t-card" style={{ flex: 1 }}>
            <div className="t-card-head">
              <h3>SLA performance</h3>
              <span className="t-card-sub">This week</span>
            </div>
            <div className="t-card-body">
              {slaRows.length === 0
                ? <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>{data.loading ? 'Loading…' : 'No SLA data yet.'}</span>
                : <StackedBar rows={slaRows} />}
            </div>
          </div>
          <div className="t-card" style={{ flex: 1 }}>
            <div className="t-card-head">
              <h3>Top tags</h3>
              <span className="t-card-sub">By ticket count</span>
            </div>
            <div className="t-card-body">
              {topTags.length === 0 ? (
                <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>No tags yet.</span>
              ) : (
                <StackedBar rows={topTags.map((t, i) => ({
                  label: '#' + t.label, value: t.value, max: tagMax, color: tagColors[i % tagColors.length],
                }))} />
              )}
            </div>
          </div>
          <div className="t-card" style={{ flex: 1 }}>
            <div className="t-card-head">
              <h3>CSAT distribution</h3>
              <span className="t-card-sub">{data.csatTotal} ratings</span>
            </div>
            <div className="t-card-body">
              {csatData.length === 0
                ? <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>{data.loading ? 'Loading…' : 'No ratings yet.'}</span>
                : <DonutChart data={csatData} />}
            </div>
          </div>
        </div>

        {/* Agent performance table */}
        <div className="t-card">
          <div className="t-card-head">
            <h3>Agent performance</h3>
            <span className="t-card-sub">Ranked by tickets handled · {range} window</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
          <table className="t-table t-table-no-hover" style={{ minWidth: 520 }}>
            <thead>
              <tr>
                <th>Agent</th>
                <th>Handled</th>
                <th>Avg response</th>
                <th>CSAT</th>
                <th>SLA met</th>
                <th>Bounced</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ color: 'var(--ink-3)', fontSize: 13, padding: '16px' }}>
                    {data.loading ? 'Loading…' : 'No agent activity for this window.'}
                  </td>
                </tr>
              ) : agents.map((a, i) => (
                <tr key={i} style={{ cursor: 'default' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center',
                        background: a.color, color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0,
                      }}>{a.initials}</div>
                      <span style={{ fontWeight: 500 }}>{a.name}</span>
                    </div>
                  </td>
                  <td><span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{a.handled}</span></td>
                  <td><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>{a.avg}</span></td>
                  <td><span style={{ color: 'var(--ink-4)', fontSize: 13 }}>—</span></td>
                  <td><span style={{ color: 'var(--ink-4)', fontSize: 13 }}>—</span></td>
                  <td><span style={{ color: 'var(--ink-4)', fontSize: 13 }}>—</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}
